import {
  Badge,
  Card,
  cn,
  healthAttentionIconTileClass,
  healthOkIconSoftClass,
} from '@up2stay/up2promo-ui';
import {
  Bell,
  CircleCheck,
  Link2,
  MessageSquareReply,
  ScanSearch,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';

import {
  useChannelLocations,
  useReputationPresets,
} from '@/core/hooks/use-reputation';
import { useLocations } from '@/core/hooks/use-locations';
import {
  getLocationConnectionStatus,
  GOOGLE_REVIEW_PROVIDER,
} from '@/core/lib/review-channel-setup';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type StepState = 'done' | 'recommended' | 'todo' | 'soon';

type Step = {
  key: string;
  icon: LucideIcon;
  title: string;
  detail: string;
  state: StepState;
};

const ICON_TONE: Record<StepState, string> = {
  done: healthOkIconSoftClass,
  recommended: healthAttentionIconTileClass,
  todo: healthAttentionIconTileClass,
  soon: 'bg-muted text-muted-foreground',
};

/**
 * Mobile progress bar: one segment per step that counts towards "x von y",
 * filled only when done, so the bar matches the text (DEV-771).
 */
export function setupProgressSegments(states: StepState[]): boolean[] {
  return states.filter((state) => state !== 'soon').map((s) => s === 'done');
}

/** Setup progress derived from live workspace state (connections → analysis → replies → alerts). */
function useSetupSteps(): { steps: Step[]; recommendation: string | null } {
  const dict = useDictionary();
  const s = dict.reputation.setup.summary;
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations(workspaceId, { limit: 100 });
  const { data: channels = [] } = useChannelLocations(workspaceId);
  const { data: presets = [] } = useReputationPresets(workspaceId);

  return useMemo(() => {
    const total = locations.length;
    const linked = locations.filter(
      (loc) =>
        getLocationConnectionStatus(loc, channels, GOOGLE_REVIEW_PROVIDER) ===
        'linked',
    ).length;
    const optedOut = locations.filter(
      (loc) =>
        getLocationConnectionStatus(loc, channels, GOOGLE_REVIEW_PROVIDER) ===
        'opt_out',
    ).length;
    const connected = total > 0 && linked + optedOut === total;

    const presetId = workspace?.defaultReputationPreset ?? null;
    const presetLabel = presets.find((p) => p.id === presetId)?.label;
    const overrides = locations.filter(
      (l) => l.reputationPreset != null,
    ).length;

    const autoDrafts = workspace?.autoReplyConfig?.enabled === true;

    const steps: Step[] = [
      {
        key: 'connections',
        icon: Link2,
        title: s.connections.title,
        detail: s.connections.detail
          .replace('{linked}', String(linked))
          .replace('{total}', String(total)),
        state: connected ? 'done' : 'todo',
      },
      {
        key: 'analysis',
        icon: ScanSearch,
        title: s.analysis.title,
        detail: presetLabel
          ? s.analysis.detail
              .replace('{preset}', presetLabel)
              .replace('{count}', String(overrides))
          : s.analysis.detailNone,
        state: presetLabel ? 'done' : 'todo',
      },
      {
        key: 'responses',
        icon: MessageSquareReply,
        title: s.responses.title,
        detail: autoDrafts ? s.responses.detailOn : s.responses.detailOff,
        state: autoDrafts ? 'done' : 'recommended',
      },
      {
        key: 'alerts',
        icon: Bell,
        title: s.alerts.title,
        detail: s.alerts.detail,
        state: 'soon',
      },
    ];
    const next = steps.find((step) => step.state === 'recommended');
    return { steps, recommendation: next ? s.recommendation : null };
  }, [locations, channels, presets, workspace, s]);
}

export function ReputationSetupSummary() {
  const dict = useDictionary();
  const s = dict.reputation.setup.summary;
  const { steps, recommendation } = useSetupSteps();
  const actionable = steps.filter((step) => step.state !== 'soon');
  const doneCount = actionable.filter((step) => step.state === 'done').length;

  return (
    <>
      {/* Desktop: one tile per step */}
      <div className="hidden grid-cols-2 gap-3 md:grid xl:grid-cols-4">
        {steps.map((step) => {
          const Icon = step.icon;
          const soon = step.state === 'soon';
          return (
            <Card
              key={step.key}
              className={cn(
                'p-4',
                soon && 'border-dashed bg-[color:var(--surface-2)] shadow-none',
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'flex size-[30px] items-center justify-center rounded-[9px]',
                    ICON_TONE[step.state],
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                </span>
                {step.state === 'done' ? (
                  <CircleCheck
                    className="size-[18px] text-[color:var(--ok)]"
                    aria-label={s.done}
                  />
                ) : step.state === 'soon' ? (
                  <Badge variant="secondary">{s.soon}</Badge>
                ) : (
                  <Badge variant="warning">
                    {step.state === 'recommended' ? s.recommended : s.todo}
                  </Badge>
                )}
              </div>
              <div
                className={cn(
                  'mt-3 text-sm font-bold',
                  soon && 'text-muted-foreground',
                )}
              >
                {step.title}
              </div>
              <div className="text-muted-foreground mt-0.5 text-xs">
                {step.detail}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Mobile: single progress card */}
      <Card className="p-4 md:hidden">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">
            {s.progress
              .replace('{done}', String(doneCount))
              .replace('{total}', String(actionable.length))}
          </span>
          <span className="text-muted-foreground font-mono text-xs">
            {Math.round((doneCount / Math.max(1, actionable.length)) * 100)} %
          </span>
        </div>
        <div className="mt-2.5 flex gap-1" aria-hidden>
          {setupProgressSegments(actionable.map((step) => step.state)).map(
            (done, index) => (
              <span
                key={actionable[index]?.key ?? index}
                className={cn(
                  'h-1.5 flex-1 rounded-full',
                  done ? 'bg-[color:var(--ok)]' : 'bg-muted',
                )}
              />
            ),
          )}
        </div>
        {recommendation ? (
          <p className="text-muted-foreground mt-2.5 text-xs">
            {recommendation}
          </p>
        ) : null}
      </Card>
    </>
  );
}
